import type { ReactNode } from 'react';
import React, { useState } from 'react';

import { REQUEST_FROM } from 'store/reviewList/types';

import { Divider } from '@travel/design-system/core/src/components/Divider';
import { L10nDate } from '@travel/i18n';
import cx from '@travel/icons/_utils/classnames';
import { CheckInCircle, More } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import Media from '@travel/traveler-core/components/Media';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import {
  YEAR_MONTH_DAY_KANJI_DATE,
  YEAR_MONTH_KANJI_DATE,
} from '@travel/traveler-core/utils/dateTime';
import { ImagePlaceHolder, Link, StarRatingText } from '@travel/ui';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';

import CategoryScore from 'components/CategoryScore';
import Dialog from 'components/CustomDialog/Dialog';
import MediaList from 'components/MediaList';
import OptionsDropdown from 'components/OptionsDropdown';
import ProviderReplySection from 'components/ProviderReplySection';
import ReviewCardWrapper from 'components/ReviewCardWrapper';
import ItemRatePlanDetails from 'components/ReviewCardWrapper/components/ItemRatePlanDetails';
import ReviewStatsControls from 'components/ReviewCardWrapper/components/ReviewStatsControls';
import ReviewTitleDescription from 'components/ReviewCardWrapper/components/ReviewTitleDescription';
import useLogin from 'hooks/useLogin';
import { QUESTION_ITEM_LABELS } from 'pages/PostReview/components/QuestionForm/labels';
import { getIsLoggedIn } from 'store/authCode/selectors';
import { getIsViewingOtherUser } from 'store/profileById/selector';
import { updateReviewAnonymity } from 'store/reviewAnonymity/actions';
import { deleteReviewById } from 'store/reviewDelete/actions';
import { fetchReviewList } from 'store/reviewList/actions';
import { resetReviewList } from 'store/reviewList/slice';
import { reportReviewById } from 'store/reviewReport/actions';
import { fetchUserProfile } from 'store/userProfile/actions';

import { pushLocation } from 'store/__router/actions';

import { DOMESTIC_DOMAIN_URL, REVIEW_LIST_LOAD_MORE_LIMIT } from 'constants/common';
import { AuthorType, MediaType } from 'store/apiTypes';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  COMMON,
  PostToggleOptionId,
  POST_TOGGLE_OPTIONS,
  POST_TOGGLE_POPUP_LABELS,
} from '../../labels';

import styles from './myReviewCard.module.scss';

import type { SortBy } from 'store/reviewList/types';
import type { UserProfile, Review, Category } from 'store/apiTypes';
type Props = {
  reviewData: Review;
  onClickMedia?: () => void;
  userProfileInfo: UserProfile;
};

type DialogConfigType = {
  buttonLabelPrimary: ReactNode;
  buttonLabelSecondary?: ReactNode;
  onClickPrimary: () => void;
  onClickSecondary?: () => void;
  content: ReactNode;
  widthClass?: string;
  customActionClassName?: string;
};

export default function MyReviewCard({ reviewData, onClickMedia, userProfileInfo }: Props) {
  const { isPC } = useDeviceTypes();
  const { isOpen, onOpen, onClose } = useDialogHandler(false);
  const isLogin = useAppSelector(getIsLoggedIn);
  const [dialogConfig, setDialogConfig] = useState<DialogConfigType | null>(null);
  const [isUpdatingReview, setUpdatingReview] = useState(false);

  const isViewingOtherUser = useAppSelector(getIsViewingOtherUser);
  const dispatch = useAppDispatch();
  const { onClickLogin } = useLogin();

  const VerticalDivider = () => {
    return <div className={styles.verticalDivider}></div>;
  };

  const filterItems = (key: Category) => {
    return reviewData.questions?.find((item) => item.category === key)?.code;
  };

  const providerId = reviewData.provider?.id;
  const providerReply = reviewData.replies?.find(
    (reply) => reply?.authorType === AuthorType.PROVIDER,
  );

  const hasProviderReply = reviewData?.replies?.some(
    (reply) => reply?.authorType === AuthorType.PROVIDER,
  );
  const isAnonymous = reviewData?.isAnonymous;
  const hasHideReason = reviewData?.hideReason;

  const fetchReviewListQuery = {
    sortBy: 'MY_REVIEW_PAGE' as SortBy,
    offset: 0,
    limit: REVIEW_LIST_LOAD_MORE_LIMIT,
    userId: String(userProfileInfo.userId),
    requestFrom: REQUEST_FROM.MY_REVIEW,
  };

  const resetAndFetchReviewList = async () => {
    dispatch(resetReviewList());
    await dispatch(fetchReviewList(fetchReviewListQuery));
  };

  const handleDeleteByReviewId = async (reviewId: string) => {
    setUpdatingReview(true);
    const resultAction = await dispatch(deleteReviewById(reviewId));

    if (deleteReviewById.fulfilled.match(resultAction)) {
      onClose();
      await dispatch(fetchReviewList(fetchReviewListQuery));
      await dispatch(fetchUserProfile());
    }

    setUpdatingReview(false);
  };

  const handleReportByReviewId = async (reviewId: string) => {
    setUpdatingReview(true);
    const resultAction = await dispatch(reportReviewById(reviewId));

    if (reportReviewById.fulfilled.match(resultAction)) {
      onClose();
      // Report Success popup
      setDialogConfig({
        buttonLabelPrimary: (
          <Translate id={POST_TOGGLE_POPUP_LABELS.reportSuccess.reportConfirmBtn} />
        ),
        onClickPrimary: () => onClose(),
        content: (
          <div className={styles.reportSuccessContent}>
            <CheckInCircle size={48} color={'cilantro'} />
            <Translate
              id={POST_TOGGLE_POPUP_LABELS.reportSuccess.title}
              className={styles.reportSuccessTitle}
            />
            <Translate
              id={POST_TOGGLE_POPUP_LABELS.reportSuccess.description}
              className={styles.reportSuccessDescription}
            />
          </div>
        ),
        widthClass: `${styles.reportSuccessWidth}`,
        customActionClassName: styles.reportSuccessActionButton,
      });
      onOpen();
    }

    setUpdatingReview(false);
  };

  const handleAnonymityByReviewId = async (reviewId: string) => {
    setUpdatingReview(true);
    const resultAction = await dispatch(
      updateReviewAnonymity({ reviewId, anonymous: !isAnonymous }),
    );

    if (updateReviewAnonymity.fulfilled.match(resultAction)) {
      resetAndFetchReviewList();
    }

    setUpdatingReview(false);
  };

  // Toggle Options
  const openDeletePopup = () => {
    setDialogConfig({
      buttonLabelPrimary: <Translate id={COMMON.cancelButton} />,
      buttonLabelSecondary: <Translate id={POST_TOGGLE_POPUP_LABELS.deletePost.deleteConfirmBtn} />,
      onClickPrimary: () => onClose(),
      onClickSecondary: () => {
        handleDeleteByReviewId(reviewData?.id);
        onClose();
      },
      content: (
        <div className={styles.deletePostContent}>
          <p>
            <Translate id={POST_TOGGLE_POPUP_LABELS.deletePost.title} />
          </p>
          <p>
            <Translate id={POST_TOGGLE_POPUP_LABELS.deletePost.description} />
          </p>
        </div>
      ),
    });
    onOpen();
  };

  const openReportPopup = () => {
    if (!isLogin) {
      onClickLogin();

      return;
    }

    setDialogConfig({
      buttonLabelPrimary: <Translate id={POST_TOGGLE_POPUP_LABELS.reportPost.reportConfirmBtn} />,
      buttonLabelSecondary: <Translate id={COMMON.cancelButton} />,
      onClickPrimary: () => {
        handleReportByReviewId(reviewData?.id);
        onClose();
      },
      onClickSecondary: () => onClose(),
      content: (
        <div className={styles.deletePostContent}>
          <p>
            <Translate id={POST_TOGGLE_POPUP_LABELS.reportPost.title} />
          </p>
          <p>
            <Translate id={POST_TOGGLE_POPUP_LABELS.reportPost.description} />
          </p>
        </div>
      ),
    });
    onOpen();
  };
  const handleEdit = () => {
    dispatch(
      pushLocation({
        pathname: '/entry/edit',
        search: `reviewId=${reviewData?.id}`,
      }),
    );
  };
  const handlePostAnonymity = () => {
    handleAnonymityByReviewId(reviewData?.id);
  };

  const handlerMap: Record<PostToggleOptionId, () => void> = {
    [PostToggleOptionId.PRIVATE]: handlePostAnonymity,
    [PostToggleOptionId.PUBLIC]: handlePostAnonymity,
    [PostToggleOptionId.EDIT]: handleEdit,
    [PostToggleOptionId.DELETE]: openDeletePopup,
    [PostToggleOptionId.REPORT]: openReportPopup,
  };

  const applicableOptionIds: string[] = POST_TOGGLE_OPTIONS.map((option) => option.id).filter(
    (id) => {
      if (id === PostToggleOptionId.REPORT) return !isPC && isViewingOtherUser;
      if (id === PostToggleOptionId.EDIT)
        return !hasProviderReply && !isViewingOtherUser && !hasHideReason;
      if (id === PostToggleOptionId.PRIVATE)
        return !isAnonymous && !isViewingOtherUser && !hasHideReason;
      if (id === PostToggleOptionId.PUBLIC)
        return isAnonymous && !isViewingOtherUser && !hasHideReason;
      if (id === PostToggleOptionId.DELETE)
        return (!isViewingOtherUser && !hasHideReason) || hasHideReason;
      // return true; // always include others options from POST_TOGGLE_OPTIONS (options with no conditions)
    },
  );

  const filteredOptions = POST_TOGGLE_OPTIONS.filter((option) =>
    applicableOptionIds.includes(option.id),
  );

  const optionsWithHandlers = filteredOptions.map((option) => ({
    ...option,
    label: <Translate id={option.label} />,
    onClickHandler: handlerMap[option.id] ?? (() => {}),
  }));
  const purpose = filterItems('PURPOSE' as Category);
  const companion = filterItems('COMPANION' as Category);

  return (
    <ReviewCardWrapper>
      <ReviewCardWrapper.PrimaryInfo>
        <>
          <div className={styles.providerInfoWrapper}>
            {reviewData.provider?.thumbnailUrl ? (
              <Media
                media={{
                  url: reviewData.provider?.thumbnailUrl,
                  type: MediaType.IMAGE,
                  alt: 'providerIcon',
                }}
                className={styles.providerImage}
                isDisableSrcSetCache
              />
            ) : (
              <ImagePlaceHolder className={styles.providerImage} />
            )}
            <div className={styles.providerDetails}>
              <Link
                url={`${DOMESTIC_DOMAIN_URL}/HOTEL/${providerId}/${providerId}.html`}
                label={reviewData.provider?.name}
                haveUnderline
                className={styles.providerName}
              />
            </div>
          </div>
          {isPC && (
            <ItemRatePlanDetails
              item={reviewData.reservation?.item}
              ratePlan={reviewData.reservation?.plan}
              providerId={providerId}
              className={styles.itemRatePlanDetails}
            />
          )}
        </>
      </ReviewCardWrapper.PrimaryInfo>
      <ReviewCardWrapper.ReviewDetails>
        <>
          <div className={cx(styles.ratingAndTravelerDetails)}>
            <div className={styles.headerRow}>
              <div className={styles.rating}>
                <StarRatingText ratingValue={reviewData?.overallScore} iconSize={16} />
                <div>
                  <L10nDate
                    value={reviewData.postDateTime}
                    format={YEAR_MONTH_DAY_KANJI_DATE}
                    isDisplayAsLocalTime
                  />
                  <Translate id="Review.Common.PostDate" />
                </div>
              </div>
              {/* Toggle review menu and spinner Loader */}
              {!isViewingOtherUser && isUpdatingReview ? (
                <div className={styles.spinnerWrapper}>
                  <LoadingCircle classType="primary" />
                </div>
              ) : (
                <>
                  {!(isViewingOtherUser && isPC) && (
                    <OptionsDropdown
                      trigger={<More size={24} />}
                      dropDownOptions={optionsWithHandlers}
                      menuTriggerClass={styles.meatballIcon}
                      dropdownItemClass={isViewingOtherUser ? styles.reportDropdownWidth : ''}
                      dropdownWrapperClass={!isPC ? styles.dropdownTopPosition : ''}
                    />
                  )}
                </>
              )}
            </div>
            <div className={styles.travelerDetails}>
              {purpose !== undefined && QUESTION_ITEM_LABELS['PURPOSE'][purpose] ? (
                <>{QUESTION_ITEM_LABELS['PURPOSE'][purpose]}</>
              ) : (
                <>-</>
              )}

              <VerticalDivider />
              {companion && QUESTION_ITEM_LABELS['COMPANION'][companion] ? (
                <>{QUESTION_ITEM_LABELS['COMPANION'][companion]}</>
              ) : (
                <>-</>
              )}
              {reviewData.reservation?.checkInDate && (
                <>
                  <VerticalDivider />
                  <Translate
                    id="Review.Common.StayMonth"
                    data={{
                      staymonth: (
                        <L10nDate
                          value={reviewData.reservation?.checkInDate}
                          format={YEAR_MONTH_KANJI_DATE}
                        />
                      ),
                    }}
                  />
                </>
              )}
            </div>
          </div>
          {reviewData.media.length ? (
            <MediaList
              reviewMedia={reviewData}
              onClickMedia={onClickMedia}
              mediaCardClassName={styles.clickable}
              layout={isPC ? 'middle' : 'grid'}
            />
          ) : null}
          {(reviewData.title || reviewData.comment || !isPC) && (
            <div className={cx(styles.titleDescriptionContainer, styles.gridItem)}>
              <ReviewTitleDescription
                title={reviewData.title}
                description={reviewData.comment}
                item={reviewData.reservation?.item}
                ratePlan={reviewData.reservation?.plan}
                providerId={providerId}
              />
            </div>
          )}
          <div className={styles.categoryScoreContainer}>
            <Divider className={styles.divider} />
            {reviewData.scores.length ? (
              <CategoryScore questions={reviewData.questions} categoryRatings={reviewData.scores} />
            ) : null}
            {!isPC && <Divider className={styles.divider} />}
          </div>
          <div className={styles.likeViewProviderReplyContainer}>
            <ReviewStatsControls
              likeCount={reviewData.likeCount}
              viewCount={reviewData.viewCount}
              reviewId={reviewData.id}
              isLiked={reviewData.isLiked}
              openReportPopup={openReportPopup}
              isUpdatingReview={isUpdatingReview}
              shouldShowPrivateTag={reviewData.isAnonymous && !isViewingOtherUser}
              shouldShowReportButton={isPC && isViewingOtherUser}
            />
            {providerReply && (
              <ProviderReplySection
                replyText={providerReply.content}
                providerName={providerReply.authorName}
                datePosted={
                  <L10nDate
                    value={providerReply.postDate}
                    format={YEAR_MONTH_DAY_KANJI_DATE}
                    isDisplayAsLocalTime
                  />
                }
                className={styles.providerReply}
              />
            )}
          </div>
        </>
      </ReviewCardWrapper.ReviewDetails>

      {dialogConfig && (
        <Dialog
          isOpen={isOpen}
          buttonLabelPrimary={dialogConfig.buttonLabelPrimary}
          buttonLabelSecondary={dialogConfig.buttonLabelSecondary}
          onClickPrimary={dialogConfig.onClickPrimary}
          onClickSecondary={dialogConfig.onClickSecondary}
          className={dialogConfig.widthClass || styles.widthPopup}
          dialogWrapperClassName={styles.deletePostContent}
          actionClassName={dialogConfig.customActionClassName || styles.actionClassName}
          overlayClassName={styles.dialogOverlay}
          contentParentClassName={styles.popupContentParentClassName}
        >
          {dialogConfig.content}
        </Dialog>
      )}
    </ReviewCardWrapper>
  );
}
